import type { Address } from "viem"
import type { Config } from "wagmi"

import type { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import type { IEventBus } from "@/domains/events"
import type { IEmissionsConfig, IGaugeRepository } from "@/domains/gauge"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { ILockLookup, ILockRepository } from "@/domains/lock/interfaces"
import type { ManagerStore } from "@/domains/manager/manager-store"
import type { IVoteStore } from "@/domains/vote"
import type { StrategyManagerStore } from "@/domains/vote-optimizer"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { OrchestratedQuery } from "@/domains/library"
import { SelectedPowerStore } from "@/domains/selected-power"
import { SelectedPowerOrchestrator } from "@/domains/selected-power/selected-power-orchestrator"

import type { VotePageNamespace } from "./types"
import { SuperVoterAccessOrchestrator } from "./super-voter-access-orchestrator"
import { VotePageStore } from "./vote-page-store"

export type VotePageNamespaceFactoryParams = {
  storage: IStorageBackend
  urlParams: IUrlParams
  chainSlug: string
  gaugeRepository: IGaugeRepository
  voteStore: IVoteStore
  userLockRepository: ILockRepository
  managerLockRepository: ILockRepository
  managerStore: ManagerStore
  nativeTokenSymbol: string
  wrappedNativeTokenSymbol: string
  lockLookup?: ILockLookup
  emissionsConfig?: IEmissionsConfig
  orchestratedQueryStore: OrchestratedQueryStore
  walletStore: IWalletConnectionStore
  eventBus: IEventBus
  config: Config
  chainId: number
  veNftAddress: Address
  superVoterAddress: Address
  strategyManagerStore?: StrategyManagerStore
}

export const VotePageNamespaceFactory = {
  create({
    storage,
    urlParams,
    chainSlug,
    gaugeRepository,
    voteStore,
    userLockRepository,
    managerLockRepository,
    managerStore,
    nativeTokenSymbol,
    wrappedNativeTokenSymbol,
    lockLookup,
    emissionsConfig,
    orchestratedQueryStore,
    walletStore,
    eventBus,
    config,
    chainId,
    veNftAddress,
    superVoterAddress,
    strategyManagerStore,
  }: VotePageNamespaceFactoryParams): VotePageNamespace {
    // Create SelectedPowerStore first - independent domain store
    const selectedPowerStore = new SelectedPowerStore(
      userLockRepository,
      managerLockRepository,
      managerStore,
      eventBus,
      gaugeRepository,
    )

    const votePageStore = new VotePageStore(
      storage,
      urlParams,
      chainSlug,
      gaugeRepository,
      voteStore,
      nativeTokenSymbol,
      wrappedNativeTokenSymbol,
    )

    // Inject dependencies into VotePageStore
    votePageStore.selectedPowerStore = selectedPowerStore
    votePageStore.lockLookup = lockLookup
    votePageStore.emissionsConfig = emissionsConfig

    // Initialize the store (this will trigger auto-select and initialize votingGauges)
    votePageStore.initialize()

    // Create SelectedPowerOrchestrator to handle wallet events
    const selectedPowerOrchestrator = new SelectedPowerOrchestrator(selectedPowerStore, eventBus)
    selectedPowerOrchestrator.initialize()

    // Create SuperVoterAccessOrchestrator
    // Note: strategyManagerStore may be undefined if VoteOptimizer hasn't been created yet
    // It will be injected after VoteOptimizer is created in ScaleApp via setStrategyManagerStore()
    const orchestrator = new SuperVoterAccessOrchestrator(
      votePageStore,
      orchestratedQueryStore,
      walletStore,
      eventBus,
      config,
      chainId,
      veNftAddress,
      superVoterAddress,
      strategyManagerStore,
    )

    // Create access check query with 15 minute interval (900000 ms)
    const accessQuery = new OrchestratedQuery<boolean>(
      "SuperVoter Access Check",
      (query) => orchestrator.onCheckAccessTrigger(query),
      900_000, // 15 minutes
      orchestrator.onCheckAccessQuerySuccess.bind(orchestrator),
      orchestrator.onCheckAccessQueryFailed.bind(orchestrator),
      orchestratedQueryStore,
    )

    // Initialize orchestrator with the query
    orchestrator.initialize(accessQuery)

    // Start query if wallet is connected
    const connectedAddress = walletStore.getConnectedAddress()
    if (connectedAddress) {
      accessQuery.start()
      // Immediately check access on initialization if wallet is already connected
      accessQuery.forceExecute()
    }

    return {
      Store: votePageStore,
      Orchestrator: orchestrator,
      SelectedPowerStore: selectedPowerStore,
    }
  },
}
